import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

const GET_DEFAULT_SET_QUESTIONS = gql`
  query GetDefaultSetQuestions {
    myQuestionSets {
      success
      message
      questionSets {
        id
        name
        isDefault
        questionCount
        items {
          id
          questionId
          question {
            id
            title
            description
            type
            options {
              text
              order
            }
            tags {
              id
              name
              description
            }
            visibility
            secret
          }
          scoring {
            weight
            isRequired
            unansweredRequiredScore
            optionScores {
              optionIndex
              score
            }
          }
        }
      }
    }
  }
`;

const GET_ACTIVE_TAGS = gql`
  query GetActiveTags {
    activeTags {
      success
      message
      tags {
        id
        name
        description
      }
    }
  }
`;

const CREATE_QUESTION = gql`
  mutation CreateQuestion($input: CreateQuestionInput!) {
    createQuestion(input: $input) {
      success
      message
      question {
        id
        title
        description
        type
        options {
          text
          order
        }
        tags {
          id
          name
          description
        }
        visibility
      }
      errors {
        field
        message
      }
    }
  }
`;

const REMOVE_QUESTION_FROM_DEFAULT_SET = gql`
  mutation RemoveQuestionFromDefaultSet($questionId: ID!) {
    removeQuestionFromDefaultSet(questionId: $questionId) {
      success
      message
      errors {
        field
        message
      }
    }
  }
`;

const UPDATE_QUESTION_SET_ITEM = gql`
  mutation UpdateQuestionSetItem($input: UpdateQuestionSetItemInput!) {
    updateQuestionSetItem(input: $input) {
      success
      message
      errors {
        field
        message
      }
    }
  }
`;

const GET_MY_TESTS = gql`
  query GetMyTests {
    myTests {
      success
      message
      tests {
        id
        name
        description
        status
        isDefault
        attemptCount
        completedAttemptCount
        passCount
        averageScore
        config {
          questionCount
          timeLimit
          allowRetake
        }
        scoreRanges {
          minScore
          maxScore
          message
          isPassed
        }
        questionSet {
          id
          name
          questionCount
        }
      }
      errors {
        field
        message
      }
    }
  }
`;

const QuestionBankContent = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRequired, setFilterRequired] = useState(null); // null: 全部, true: 僅必答題, false: 僅非必答題
  const [sortBy, setSortBy] = useState('weight'); // 'weight': 按權重, 'title': 按標題
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc': 降序, 'asc': 升序
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    title: '',
    description: '',
    type: 'SINGLE_CHOICE',
    options: [
      { text: '', order: 0, score: 0 },
      { text: '', order: 1, score: 0 }
    ],
    tags: [],
    visibility: 'PRIVATE',
    weight: 1,
    isRequired: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    optionScores: [],
    weightExponent: 0,
    isRequired: false,
    unansweredRequiredScore: 0
  });
  
  const { data, loading, error } = useQuery(GET_DEFAULT_SET_QUESTIONS);
  const { data: testsData, loading: testsLoading } = useQuery(GET_MY_TESTS);
  const { data: tagsData } = useQuery(GET_ACTIVE_TAGS);

  const [createQuestion] = useMutation(CREATE_QUESTION, {
    refetchQueries: [{ query: GET_DEFAULT_SET_QUESTIONS }]
  });
  
  const [removeQuestionFromDefaultSet, { loading: removeLoading }] = useMutation(REMOVE_QUESTION_FROM_DEFAULT_SET, {
    refetchQueries: [{ query: GET_DEFAULT_SET_QUESTIONS }]
  });
  
  const [updateQuestionSetItem, { loading: updateLoading }] = useMutation(UPDATE_QUESTION_SET_ITEM, {
    refetchQueries: [{ query: GET_DEFAULT_SET_QUESTIONS }]
  });

  const defaultSet = data?.myQuestionSets?.questionSets?.find(set => set.isDefault);
  const questionItems = defaultSet?.items || [];
  
  // 過濾：關鍵字和必答題
  let filteredQuestionItems = questionItems.filter(item => {
    if (!item.question) return false;
    
    // 關鍵字過濾
    const matchesKeyword = !searchTerm || (
      item.question.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.question.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // 必答題過濾
    const matchesRequired = filterRequired === null || item.scoring.isRequired === filterRequired;
    
    return matchesKeyword && matchesRequired;
  });
  
  // 排序：按權重或標題
  filteredQuestionItems = [...filteredQuestionItems].sort((a, b) => {
    if (sortBy === 'weight') {
      const weightA = a.scoring?.weight || 0;
      const weightB = b.scoring?.weight || 0;
      return sortOrder === 'desc' ? weightB - weightA : weightA - weightB;
    } else if (sortBy === 'title') {
      const titleA = a.question?.title || '';
      const titleB = b.question?.title || '';
      return sortOrder === 'desc' 
        ? titleB.localeCompare(titleA, 'zh-TW')
        : titleA.localeCompare(titleB, 'zh-TW');
    }
    return 0;
  });

  const handleCreateQuestion = () => {
    console.log('Creating new question');
    setShowCreateModal(true);
  };

  const defaultTest = testsData?.myTests?.tests?.find(test => test.isDefault);
  
  const handleStartTest = (testId) => {
    navigate(`/test/${testId}`);
  };

  const handleEditTest = () => {
    navigate(`/tests`);
  };

  const handleRemoveQuestion = async (questionId) => {
    console.log('Removing question from default set:', questionId);
    
    if (!window.confirm('確定要從預設題庫中移除這個問題嗎？')) {
      return;
    }
    
    try {
      const result = await removeQuestionFromDefaultSet({
        variables: { questionId }
      });
      
      const response = result.data.removeQuestionFromDefaultSet;
      
      if (response.success) {
        console.log('Question removed successfully');
      } else {
        console.log('Remove question failed:', response.message);
        alert('移除問題失敗：' + response.message);
      }
    } catch (error) {
      console.error('Remove question error:', error);
      alert('移除問題失敗，請稍後再試');
    }
  };

  const handleEditScoring = (item) => {
    console.log('Editing scoring for item:', item);
    
    if (!item.question) {
      console.error('Cannot edit scoring: question is null');
      alert('無法編輯配分：問題不存在');
      return;
    }
    
    console.log('Item scoring:', item.scoring);
    console.log('Weight type:', typeof item.scoring.weight, 'Value:', item.scoring.weight);
    
    // 如果已經在編輯這個item，則關閉編輯
    if (editingItem && editingItem.id === item.id) {
      setEditingItem(null);
      return;
    }
    
    setEditingItem(item);
    
    const optionScores = [];
    if (item.question.options) {
      item.question.options.forEach((option, index) => {
        let score = 0;
        
        if (item.scoring.optionScores) {
          if (typeof item.scoring.optionScores === 'object' && item.scoring.optionScores instanceof Map) {
            score = item.scoring.optionScores.get(index.toString()) || 0;
          } else if (Array.isArray(item.scoring.optionScores)) {
            const scoreObj = item.scoring.optionScores.find(s => s.optionIndex === index);
            if (scoreObj) {
              score = typeof scoreObj.score === 'object' ? (scoreObj.score.score || 0) : scoreObj.score;
            }
          } else if (typeof item.scoring.optionScores === 'object') {
            const scoreValue = item.scoring.optionScores[index] || item.scoring.optionScores[index.toString()];
            score = typeof scoreValue === 'object' ? (scoreValue.score || 0) : (scoreValue || 0);
          }
        }
        
        optionScores.push({
          optionIndex: index,
          score: score
        });
      });
    }
    
    console.log('Initialized optionScores:', optionScores);
    
    let weight = 1;
    if (item.scoring.weight !== undefined && item.scoring.weight !== null) {
      if (typeof item.scoring.weight === 'object') {
        weight = item.scoring.weight.value || item.scoring.weight.toString() || 1;
      } else {
        weight = item.scoring.weight;
      }
    }
    
    weight = parseInt(weight) || 1;
    let weightExponent = Math.round(Math.log2(weight));
    weightExponent = Math.max(0, Math.min(10, weightExponent));
    
    console.log('Processed weight:', weight, 'Type:', typeof weight, 'Exponent:', weightExponent);
    
    setEditForm({
      optionScores,
      weightExponent: weightExponent,
      isRequired: item.scoring.isRequired || false,
      unansweredRequiredScore: item.scoring.unansweredRequiredScore !== undefined ? item.scoring.unansweredRequiredScore : 0
    });
  };
  
  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditForm({
      optionScores: [],
      weightExponent: 0,
      isRequired: false,
      unansweredRequiredScore: 0
    });
  };

  const handleEditFormChange = (field, value) => {
    console.log('Edit form field changed:', field, value);
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 映射滑動條位置到實際分數
  const mapSliderToScore = (sliderValue) => {
    if (sliderValue <= -11) return -9999;  // 左端極值
    return sliderValue;                    // -10 到 +10 之間保持原值
  };
  
  // 映射實際分數到滑動條位置
  const mapScoreToSlider = (score) => {
    if (score <= -9999) return -12;  // 極小值映射到左端
    if (score > 10) return 10;       // 超過10的映射到右端
    return score;                    // -10 到 +10 之間保持原值
  };

  const handleOptionScoreChange = (index, sliderValue) => {
    const actualScore = mapSliderToScore(parseFloat(sliderValue));
    console.log('Option score changed:', index, 'slider:', sliderValue, 'actual:', actualScore);
    
    setEditForm(prev => ({
      ...prev,
      optionScores: prev.optionScores.map((item, i) => 
        i === index ? { ...item, score: actualScore } : item
      )
    }));
  };

  const handleSaveScoring = async () => {
    console.log('Saving scoring for item:', editingItem);
    
    if (!editingItem) return;
    
    try {
      const actualWeight = Math.pow(2, editForm.weightExponent);
      
      const processedScoring = {
        optionScores: editForm.optionScores.map(score => ({
          ...score,
          score: score.score === '' ? 0 : parseFloat(score.score) || 0
        })),
        weight: actualWeight,
        isRequired: editForm.isRequired,
        unansweredRequiredScore: editForm.unansweredRequiredScore !== undefined ? parseFloat(editForm.unansweredRequiredScore) || 0 : 0
      };
      
      const result = await updateQuestionSetItem({
        variables: {
          input: {
            id: editingItem.id,
            scoring: processedScoring
          }
        }
      });
      
      const response = result.data.updateQuestionSetItem;
      
      if (response.success) {
        console.log('Scoring updated successfully');
        setEditingItem(null);
        setEditForm({
          optionScores: [],
          weightExponent: 0,
          isRequired: false,
          unansweredRequiredScore: 0
        });
      } else {
        console.log('Update scoring failed:', response.message);
        alert('更新配分失敗：' + response.message);
      }
    } catch (error) {
      console.error('Update scoring error:', error);
      alert('更新配分失敗，請稍後再試');
    }
  };

  const handleQuestionFormChange = (field, value) => {
    console.log('Question form field changed:', field, value);
    setQuestionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index, field, value) => {
    console.log('Option changed:', index, field, value, typeof value);
    if (field === 'score') {
      let processedScore;
      if (value === '' || value === null || value === undefined) {
        processedScore = '';
      } else {
        const numScore = typeof value === 'number' ? value : parseFloat(value);
        processedScore = isNaN(numScore) ? 0 : numScore;
      }
      
      setQuestionForm(prev => ({
        ...prev,
        options: prev.options.map((option, i) => 
          i === index ? { ...option, score: processedScore } : option
        )
      }));
    } else {
      setQuestionForm(prev => ({
        ...prev,
        options: prev.options.map((option, i) => 
          i === index ? { ...option, [field]: value } : option
        )
      }));
    }
  };

  const addOption = () => {
    console.log('Adding new option');
    setQuestionForm(prev => ({
      ...prev,
      options: [...prev.options, { text: '', order: prev.options.length, score: 0 }]
    }));
  };

  const removeOption = (index) => {
    console.log('Removing option:', index);
    if (questionForm.options.length > 2) {
      setQuestionForm(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index).map((option, i) => ({
          ...option,
          order: i
        }))
      }));
    }
  };

  const handleAddTag = (tagName) => {
    if (tagName && !questionForm.tags.includes(tagName)) {
      setQuestionForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagName]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setQuestionForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!questionForm.title.trim()) {
      errors.title = '問題標題不能為空';
    } else if (questionForm.title.trim().length > 500) {
      errors.title = '問題標題不能超過500字';
    }
    
    if (questionForm.description && questionForm.description.length > 1000) {
      errors.description = '問題描述不能超過1000字';
    }
    
    const validOptions = questionForm.options.filter(option => option.text.trim());
    if (validOptions.length < 2) {
      errors.options = '至少需要2個選項';
    }
    
    if (questionForm.tags.length > 10) {
      errors.tags = '標籤數量不能超過10個';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setQuestionForm({
      title: '',
      description: '',
      type: 'SINGLE_CHOICE',
      options: [
        { text: '', order: 0, score: 0 },
        { text: '', order: 1, score: 0 }
      ],
      tags: [],
      visibility: 'PRIVATE',
      weight: 1,
      isRequired: false
    });
    setFormErrors({});
  };

  const handleSubmitQuestion = async () => {
    console.log('Submitting question form:', questionForm);
    
    if (!validateForm()) {
      console.log('Form validation failed:', formErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const validOptions = questionForm.options
        .filter(option => option.text.trim())
        .map((option, index) => ({
          text: option.text.trim(),
          order: index
        }));
      
      const scoring = {
        optionScores: questionForm.options
          .filter(option => option.text.trim())
          .map((option, index) => ({
            optionIndex: index,
            score: option.score === '' ? 0 : parseFloat(option.score) || 0
          })),
        weight: questionForm.weight === '' ? 1 : parseInt(questionForm.weight) || 1,
        isRequired: questionForm.isRequired
      };
      
      const result = await createQuestion({
        variables: {
          input: {
            title: questionForm.title.trim(),
            description: questionForm.description.trim() || null,
            type: questionForm.type,
            options: validOptions,
            tags: questionForm.tags,
            visibility: questionForm.visibility,
            scoring
          }
        }
      });
      
      const response = result.data.createQuestion;
      
      if (response.success) {
        console.log('Question created successfully:', response.question);
        setShowCreateModal(false);
        resetForm();
      } else {
        console.log('Create question failed:', response.message);
        const backendErrors = {};
        response.errors.forEach(error => {
          backendErrors[error.field] = error.message;
        });
        setFormErrors(backendErrors);
      }
    } catch (error) {
      console.error('Create question error:', error);
      setFormErrors({ general: '創建問題失敗，請稍後再試' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 32px;
          border-radius: 3px;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 32px;
          border-radius: 3px;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        
        /* 負分 - 灰色滑塊 */
        input[type="range"].slider-negative::-webkit-slider-thumb {
          background: #9ca3af;
        }
        
        input[type="range"].slider-negative::-moz-range-thumb {
          background: #9ca3af;
        }
        
        input[type="range"].slider-negative:hover::-webkit-slider-thumb {
          background: #6b7280;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-negative:hover::-moz-range-thumb {
          background: #6b7280;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        /* 正分 - 黃色滑塊 */
        input[type="range"].slider-positive::-webkit-slider-thumb {
          background: #fbbf24;
        }
        
        input[type="range"].slider-positive::-moz-range-thumb {
          background: #fbbf24;
        }
        
        input[type="range"].slider-positive:hover::-webkit-slider-thumb {
          background: #f59e0b;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-positive:hover::-moz-range-thumb {
          background: #f59e0b;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        /* 權重滑塊 - 動態顏色 */
        input[type="range"].slider-weight::-webkit-slider-thumb {
          background: var(--slider-color);
        }
        
        input[type="range"].slider-weight::-moz-range-thumb {
          background: var(--slider-color);
        }
        
        input[type="range"].slider-weight:hover::-webkit-slider-thumb {
          filter: brightness(0.85);
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-weight:hover::-moz-range-thumb {
          filter: brightness(0.85);
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        /* -9999 極值 - 紅色滑塊 */
        input[type="range"].slider-extreme::-webkit-slider-thumb {
          background: #dc2626;
        }
        
        input[type="range"].slider-extreme::-moz-range-thumb {
          background: #dc2626;
        }
        
        input[type="range"].slider-extreme:hover::-webkit-slider-thumb {
          background: #b91c1c;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-extreme:hover::-moz-range-thumb {
          background: #b91c1c;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
      `}</style>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* 預設測試顯示 - 暫時隱藏 */}
      {false && !testsLoading && (
        defaultTest ? (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">預設測試</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">題目集：</span>
                    <span>{defaultTest.questionSet?.name || '預設題庫'}</span>
                  </div>
                  <div>
                    <span className="font-medium">題目數：</span>
                    <span>{defaultTest.questionSet?.questionCount || questionItems.length}</span>
                  </div>
                  <div>
                    <span className="font-medium">嘗試次數：</span>
                    <span>{defaultTest.attemptCount || 0}</span>
                  </div>
                  <div>
                    <span className="font-medium">通過次數：</span>
                    <span>{defaultTest.passCount || 0}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleStartTest(defaultTest.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  開始答題
                </button>
                <button
                  onClick={() => handleEditTest(defaultTest)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  編輯
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">預設測試</h2>
                <p className="text-sm text-gray-600">尚未創建預設測試</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/tests')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  創建測試
                </button>
              </div>
            </div>
          </div>
        )
      )}
      
      {/* 搜尋和過濾欄 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          {/* 關鍵字搜尋 */}
          <div className="flex-1 w-full md:w-auto">
            <input
              type="text"
              placeholder="搜尋題目標題或描述..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          {/* 必答題過濾 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">必答題：</label>
            <select
              value={filterRequired === null ? 'all' : filterRequired ? 'required' : 'notRequired'}
              onChange={(e) => {
                const value = e.target.value;
                setFilterRequired(value === 'all' ? null : value === 'required');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">全部</option>
              <option value="required">僅必答題</option>
              <option value="notRequired">僅非必答題</option>
            </select>
          </div>
          
          {/* 排序 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">排序：</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="weight">按權重</option>
              <option value="title">按標題</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              title={sortOrder === 'desc' ? '降序' : '升序'}
            >
              {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
          </div>
          
          {/* 取得測試連結按鈕 */}
          <div className="flex items-center">
            <button
              onClick={() => navigate('/tests')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors whitespace-nowrap"
            >
              🔗 取得測試連結
            </button>
          </div>
        </div>
        
        {/* 顯示過濾結果統計 */}
        {questionItems.length > 0 && (
          <div className="mt-3 text-sm text-gray-600">
            顯示 {filteredQuestionItems.length} / {questionItems.length} 題
          </div>
        )}
      </div>
      
      <div className="space-y-3">
        {loading || error ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">載入中...</div>
        ) : filteredQuestionItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-400">題庫中還沒有問題，去投票廣場挑選你重視的問題加入題庫吧</div>
        ) : (
          filteredQuestionItems.map(item => (
            item.question ? (
              <div key={item.id} className={`p-4 sm:p-6 overflow-hidden rounded-lg ${editingItem && editingItem.id === item.id ? 'border-2 border-purple-400 bg-white shadow-lg' : 'bg-white border border-gray-200 shadow hover:shadow-md transition-shadow'}`}>
                  {/* 標題行 */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-2 flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 break-words flex-1">{item.question.title}</h3>
                      {item.question.secret && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                          🔒 祕密
                        </span>
                      )}
                    </div>
                    <div className="ml-2 sm:ml-4 flex-shrink-0">
                      <button
                        onClick={() => handleEditScoring(item)}
                        disabled={updateLoading}
                        className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base font-medium bg-purple-400 text-white rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {editingItem && editingItem.id === item.id ? '收起' : '編輯'}
                      </button>
                    </div>
                  </div>
                  
                  {/* 描述 */}
                  {item.question.description && (
                    <div className={`text-gray-600 mb-3 break-words whitespace-pre-wrap ${
                      (!editingItem || editingItem.id !== item.id) ? 'line-clamp-3' : ''
                    }`}>
                      {item.question.description}
                    </div>
                  )}
                  
                  {/* 分隔線 */}
                  <div className="border-t border-gray-200 my-3"></div>
                  
                  {/* 權重、必答題、標籤行 - 非編輯模式顯示 */}
                  {(!editingItem || editingItem.id !== item.id) && (
                    <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">權重:</span>
                        <span className="ml-1 text-purple-600 font-semibold">{item.scoring.weight}</span>
                      </div>
                      {item.scoring.isRequired && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs font-medium">必答題</span>
                      )}
                      {item.question.tags?.length > 0 && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700">標籤:</span>
                          <span className="ml-1">{item.question.tags.map(tag => tag?.name || tag).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* 選項列表 */}
                  {item.question.options && item.question.options.length > 0 && (!editingItem || editingItem.id !== item.id) && (
                    <div className="space-y-2">
                      {item.question.options.map((option, index) => {
                        const scoreObj = item.scoring.optionScores && item.scoring.optionScores.find(s => s.optionIndex === index);
                        const score = scoreObj ? scoreObj.score : 0;
                        // 計算百分比：與AddToLibraryModal保持一致
                        let percentage;
                        const sliderMin = -12;
                        const sliderMax = 10;
                        const sliderRange = sliderMax - sliderMin; // 22
                        const zeroPosition = ((0 - sliderMin) / sliderRange) * 100; // 54.55%

                        if (score <= -9999) {
                          percentage = zeroPosition; // 完全填滿到0分位置
                        } else if (score >= 0) {
                          // 正分：從0到+10，佔據剩餘空間的比例
                          percentage = (score / 10) * (100 - zeroPosition);
                        } else {
                          // 負分：從0到-12，佔據左側空間的比例，比例到sliderMin
                          percentage = (Math.abs(score) / Math.abs(sliderMin)) * zeroPosition;
                        }
                        const isPositive = score >= 0;
                        
                        return (
                          <div key={index} className="relative border border-gray-300 rounded-lg overflow-hidden h-8 w-full">
                            {/* 中線 - 顯示在0分位置 */}
                            <div
                              className="absolute top-0 bottom-0 w-px bg-gray-400 z-0"
                              style={{ left: `${zeroPosition}%` }}
                            ></div>
                            {score !== 0 && (
                              <div
                                className={`absolute top-0 bottom-0 z-0 ${
                                  isPositive ? 'bg-yellow-100' : 'bg-gray-200'
                                }`}
                                style={{
                                  [isPositive ? 'left' : 'right']: `${isPositive ? zeroPosition : (100 - zeroPosition)}%`,
                                  width: `${percentage}%`
                                }}
                              ></div>
                            )}
                            
                            {/* 內容 */}
                            <div className="relative z-10 px-3 py-2 flex items-center justify-between">
                              <span className="text-sm text-gray-700 font-medium flex-1 mr-3 truncate">{index + 1}. {option.text}</span>
                              <span className={`text-sm font-bold whitespace-nowrap flex-shrink-0 ${
                                score === -9999 ? 'text-red-600' : isPositive ? 'text-purple-600' : 'text-gray-600'
                              }`}>
                                {score === -9999 ? '-9999' : score > 0 ? `+${score.toFixed(2)}` : score.toFixed(2)}分
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* 編輯模式 */}
                  {editingItem && editingItem.id === item.id && (
                    <>
                      {/* 權重和必答題設置 */}
                      <div className="mb-4 pb-4 border-b border-gray-300 space-y-4 w-full overflow-hidden">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            權重: <span className="text-purple-600 font-bold">{Math.pow(2, editForm.weightExponent)}</span>
                          </label>
                          {/* 權重計量條 */}
                          {(() => {
                            // 計算漸變：0 = 黑灰，10 = 黃色
                            const ratio = editForm.weightExponent / 10; // 0 到 1
                            // 背景色從灰色(229,231,235)漸變到淡黃色(254,249,195)
                            const bgR = Math.round(229 + (254 - 229) * ratio);
                            const bgG = Math.round(231 + (249 - 231) * ratio);
                            const bgB = Math.round(235 + (195 - 235) * ratio);
                            const bgColor = `rgb(${bgR}, ${bgG}, ${bgB})`;
                            
                            // 計量條從深灰(156,163,175)漸變到黃色(251,191,36)
                            const barR = Math.round(156 + (251 - 156) * ratio);
                            const barG = Math.round(163 + (191 - 163) * ratio);
                            const barB = Math.round(175 + (36 - 175) * ratio);
                            const barColor = `rgb(${barR}, ${barG}, ${barB})`;
                            
                            // 滑塊從黑色(31,41,55)漸變到深黃色(245,158,11)
                            const sliderR = Math.round(31 + (245 - 31) * ratio);
                            const sliderG = Math.round(41 + (158 - 41) * ratio);
                            const sliderB = Math.round(55 + (11 - 55) * ratio);
                            const sliderColor = `rgb(${sliderR}, ${sliderG}, ${sliderB})`;
                            
                            return (
                              <div className="relative border border-gray-300 rounded-lg overflow-hidden h-10" style={{ backgroundColor: bgColor }}>
                                {/* 填充背景 - 從左到當前位置 */}
                                <div
                                  className="absolute top-0 left-0 bottom-0"
                                  style={{
                                    width: `${(editForm.weightExponent / 10) * 100}%`,
                                    backgroundColor: barColor
                                  }}
                                ></div>
                                
                                {/* 滑動條 */}
                                <input
                                  type="range"
                                  min="0"
                                  max="10"
                                  value={editForm.weightExponent}
                                  onChange={(e) => handleEditFormChange('weightExponent', parseInt(e.target.value))}
                                  className="relative z-10 w-full h-full opacity-60 hover:opacity-100 cursor-pointer slider-weight"
                                  style={{
                                    background: 'transparent',
                                    WebkitAppearance: 'none',
                                    appearance: 'none',
                                    '--slider-color': sliderColor
                                  }}
                                />
                              </div>
                            );
                          })()}
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>1</span>
                            <span>1024</span>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            設為必答題
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditFormChange('isRequired', true)}
                              className={`flex-1 px-4 py-2 text-sm rounded-lg border-2 transition-colors ${
                                editForm.isRequired
                                  ? 'bg-purple-500 text-white border-purple-500'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                              }`}
                            >
                              是
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEditFormChange('isRequired', false)}
                              className={`flex-1 px-4 py-2 text-sm rounded-lg border-2 transition-colors ${
                                !editForm.isRequired
                                  ? 'bg-purple-500 text-white border-purple-500'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                              }`}
                            >
                              否
                            </button>
                          </div>
                        </div>

                        {/* 未作答必答題分數設定（僅在設為必答題時顯示） */}
                        {editForm.isRequired && (
                          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              未作答必答題分數
                            </label>
                            {(() => {
                              const sliderValue = mapScoreToSlider(editForm.unansweredRequiredScore);
                              const isPositive = editForm.unansweredRequiredScore >= 0;
                              
                              // 計算0分的位置百分比（滑動條範圍 -12 到 10）
                              const sliderMin = -12;
                              const sliderMax = 10;
                              const sliderRange = sliderMax - sliderMin;
                              const zeroPosition = ((0 - sliderMin) / sliderRange) * 100;
                              
                              // 計算計量條寬度百分比
                              let percentage;
                              if (editForm.unansweredRequiredScore <= -9999) {
                                percentage = zeroPosition;
                              } else if (isPositive) {
                                percentage = (editForm.unansweredRequiredScore / 10) * (100 - zeroPosition);
                              } else {
                                percentage = (Math.abs(editForm.unansweredRequiredScore) / Math.abs(sliderMin)) * zeroPosition;
                              }
                              
                              return (
                                <div className="space-y-1 w-full overflow-hidden">
                                  <div className="flex items-start justify-between text-sm gap-2 w-full">
                                    <span className="text-gray-700 font-medium flex-1 min-w-0 break-words">
                                      當此題目設為必答但未作答時，將使用此分數計算
                                    </span>
                                    <span className={`font-bold whitespace-nowrap flex-shrink-0 ${
                                      editForm.unansweredRequiredScore === -9999 ? 'text-red-600' : isPositive ? 'text-purple-600' : 'text-gray-600'
                                    }`}>
                                      {editForm.unansweredRequiredScore === -9999 ? '-9999' : editForm.unansweredRequiredScore > 0 ? `+${editForm.unansweredRequiredScore.toFixed(2)}` : editForm.unansweredRequiredScore.toFixed(2)}分
                                    </span>
                                  </div>
                                  
                                  {/* 計量條預覽 */}
                                  <div className="relative border border-gray-300 rounded-lg overflow-hidden h-8 w-full">
                                    {/* 中線 - 顯示在0分位置 */}
                                    <div 
                                      className="absolute top-0 bottom-0 w-px bg-gray-400 z-0"
                                      style={{ left: `${zeroPosition}%` }}
                                    ></div>
                                    {editForm.unansweredRequiredScore !== 0 && (
                                      <div
                                        className={`absolute top-0 bottom-0 z-0 ${
                                          isPositive ? 'bg-yellow-100' : 'bg-gray-200'
                                        }`}
                                        style={{
                                          [isPositive ? 'left' : 'right']: `${isPositive ? zeroPosition : (100 - zeroPosition)}%`,
                                          width: `${percentage}%`
                                        }}
                                      ></div>
                                    )}
                                    
                                    {/* 滑動條 */}
                                    <input
                                      type="range"
                                      min="-12"
                                      max="10"
                                      step="0.5"
                                      value={sliderValue}
                                      onChange={(e) => {
                                        const actualScore = mapSliderToScore(parseFloat(e.target.value));
                                        handleEditFormChange('unansweredRequiredScore', actualScore);
                                      }}
                                      className={`relative z-10 w-full h-full opacity-50 hover:opacity-100 cursor-pointer ${
                                        editForm.unansweredRequiredScore === -9999 ? 'slider-extreme' : isPositive ? 'slider-positive' : 'slider-negative'
                                      }`}
                                      style={{
                                        background: 'transparent',
                                        WebkitAppearance: 'none',
                                        appearance: 'none'
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-3 w-full overflow-hidden">
                        {item.question.options.map((option, index) => {
                          const currentScore = editForm.optionScores[index]?.score || 0;
                          const sliderValue = mapScoreToSlider(currentScore);
                          const isPositive = currentScore >= 0;
                          
                          // 計算0分的位置百分比（滑動條範圍 -12 到 10）
                          const sliderMin = -12;
                          const sliderMax = 10;
                          const sliderRange = sliderMax - sliderMin; // 22
                          const zeroPosition = ((0 - sliderMin) / sliderRange) * 100; // 54.55%
                          
                          // 計算計量條寬度百分比
                          let percentage;
                          if (currentScore <= -9999) {
                            percentage = zeroPosition; // 完全填滿左側到0分位置
                          } else if (isPositive) {
                            // 正分：從0到+10，佔據剩餘空間的比例
                            percentage = (currentScore / 10) * (100 - zeroPosition);
                          } else {
                            // 負分：從左邊緣向零點延伸，-12時達到零點位置，-10時比例減少
                            percentage = (Math.abs(currentScore) / Math.abs(sliderMin)) * zeroPosition;
                          }
                          
                          return (
                            <div key={index} className="space-y-1 w-full overflow-hidden">
                              <div className="flex items-start justify-between text-sm gap-2">
                                <span className="text-gray-700 font-medium flex-1 min-w-0 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                  {index + 1}. {option.text}
                                </span>
                                <span className={`font-bold whitespace-nowrap flex-shrink-0 ${
                                  currentScore === -9999 ? 'text-red-600' : isPositive ? 'text-purple-600' : 'text-gray-600'
                                }`}>
                                  {currentScore === -9999 ? '-9999' : currentScore > 0 ? `+${currentScore.toFixed(2)}` : currentScore.toFixed(2)}分
                                </span>
                              </div>
                              
                              {/* 計量條預覽 */}
                              <div className="relative border border-gray-300 rounded-lg overflow-hidden h-8 w-full">
                                {/* 中線 - 顯示在0分位置 */}
                                <div 
                                  className="absolute top-0 bottom-0 w-px bg-gray-400 z-0"
                                  style={{ left: `${zeroPosition}%` }}
                                ></div>
                                {currentScore !== 0 && (
                                  <div
                                    className={`absolute top-0 bottom-0 z-0 ${
                                      isPositive ? 'bg-yellow-100' : 'bg-gray-200'
                                    }`}
                                    style={{
                                      [isPositive ? 'left' : 'right']: `${isPositive ? zeroPosition : (100 - zeroPosition)}%`,
                                      width: `${percentage}%`
                                    }}
                                  ></div>
                                )}
                                
                                {/* 滑動條 */}
                                <input
                                  type="range"
                                  min="-12"
                                  max="10"
                                  step="0.5"
                                  value={sliderValue}
                                  onChange={(e) => handleOptionScoreChange(index, e.target.value)}
                                  className={`relative z-10 w-full h-full opacity-50 hover:opacity-100 cursor-pointer ${
                                    currentScore === -9999 ? 'slider-extreme' : isPositive ? 'slider-positive' : 'slider-negative'
                                  }`}
                                  style={{
                                    background: 'transparent',
                                    WebkitAppearance: 'none',
                                    appearance: 'none'
                                  }}
                              />
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      
                      {/* 保存和取消按鈕 */}
                      <div className="mt-4 flex gap-2 justify-between w-full">
                        <button
                          onClick={() => handleRemoveQuestion(item.questionId)}
                          disabled={removeLoading}
                          className="px-3 sm:px-4 py-2 text-sm bg-white text-orange-600 border-2 border-orange-500 rounded hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          移除
                        </button>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 sm:px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 whitespace-nowrap"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleSaveScoring}
                            disabled={updateLoading}
                            className="px-3 sm:px-4 py-2 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {updateLoading ? '保存中...' : '保存'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div key={item.id} className="p-4 sm:p-6 bg-red-50 border border-red-200 rounded-lg shadow">
                  <div className="text-red-600">
                    <div className="font-semibold">問題不存在</div>
                    <div className="text-sm">此問題已被刪除或不存在 (ID: {item.questionId})</div>
                  </div>
                  <div className="mt-2">
                    <button
                      onClick={() => handleRemoveQuestion(item.questionId)}
                      disabled={removeLoading}
                      className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      移除此項目
                    </button>
                  </div>
                </div>
              )
            ))
          )}
      </div>

      {/* 新增問題彈窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                新增問題
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    問題標題 *
                  </label>
                  <input
                    type="text"
                    value={questionForm.title}
                    onChange={(e) => handleQuestionFormChange('title', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="請輸入問題標題"
                  />
                  {formErrors.title && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    問題描述
                  </label>
                  <textarea
                    value={questionForm.description}
                    onChange={(e) => handleQuestionFormChange('description', e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="請輸入問題描述（選填）"
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    問題類型
                  </label>
                  <select
                    value={questionForm.type}
                    onChange={(e) => handleQuestionFormChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="SINGLE_CHOICE">單選題</option>
                    <option value="MULTIPLE_CHOICE">多選題</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選項 * (得分範圍: -10 到 10)
                  </label>
                  <div className="space-y-2">
                    {questionForm.options.map((option, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder={`選項 ${index + 1}`}
                        />
                        <input
                          type="number"
                          value={option.score === '' ? '' : option.score}
                          onChange={(e) => handleOptionChange(index, 'score', e.target.value)}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="得分"
                          min="-10"
                          max="10"
                          step="1"
                          inputMode="numeric"
                        />
                        {questionForm.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            className="px-2 py-2 text-red-600 hover:text-red-800"
                          >
                            刪除
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addOption}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + 新增選項
                    </button>
                  </div>
                  {formErrors.options && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.options}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      權重 (1-1024)
                    </label>
                    <input
                      type="number"
                      value={questionForm.weight === '' ? '' : questionForm.weight}
                      onChange={(e) => {
                        const value = e.target.value;
                        const processedValue = value === '' ? '' : (isNaN(parseInt(value)) ? 1 : parseInt(value));
                        handleQuestionFormChange('weight', processedValue);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                      max="1024"
                      step="1"
                      inputMode="numeric"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isRequired"
                      checked={questionForm.isRequired}
                      onChange={(e) => handleQuestionFormChange('isRequired', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-700">
                      必答題
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    標籤
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddTag(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    disabled={!tagsData?.activeTags?.tags || tagsData.activeTags.tags.length === 0}
                  >
                    <option value="">選擇標籤...</option>
                    {tagsData?.activeTags?.tags
                      ?.filter(tag => !questionForm.tags.includes(tag.name))
                      .map(tag => (
                        <option key={tag.id} value={tag.name}>
                          {tag.name}
                          {tag.description && ` - ${tag.description}`}
                        </option>
                      ))}
                  </select>
                  {questionForm.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {questionForm.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {formErrors.tags && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.tags}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    可見性
                  </label>
                  <select
                    value={questionForm.visibility}
                    onChange={(e) => handleQuestionFormChange('visibility', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="PRIVATE">私人</option>
                    <option value="PUBLIC">公開</option>
                  </select>
                </div>
              </div>

              {formErrors.general && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{formErrors.general}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  取消
                </button>
                <button
                  onClick={handleSubmitQuestion}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '新增中...' : '新增問題'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
};

export default QuestionBankContent;

